import {
    getSlidingStoryTitle,
    pickSlidingOriginalImageUrl,
} from "../../../pages/browse-sliding/slidingBrowseUtils";
import SlidingService from "../../../pages/puzzles/sliding/SlidingService";
import AdminSlidingService from "../../../admin/story/AdminSlidingService";
import { templateForGridAndLevel } from "../../../admin/story/adminSlidingUi";
import { parseSlidingStartingLayout } from "../animation/lib/slidingLayoutMoves";

function pickTemplateFromGroup(templates, { slidingId, expectedGrid, expectedLevel }) {
    const list = Array.isArray(templates) ? templates : [];
    if (list.length === 0) return null;

    if (slidingId) {
        const target = String(slidingId).trim().toLowerCase();
        const byId = list.find((t) => String(t?.id ?? "").trim().toLowerCase() === target);
        const byIdGrid = Number(byId?.gridSize ?? byId?.grid_size);
        if (byId && (!expectedGrid || byIdGrid === Number(expectedGrid))) {
            return byId;
        }
    }

    if (expectedGrid && expectedLevel) {
        const byGrid = templateForGridAndLevel(list, expectedGrid, expectedLevel);
        if (byGrid) return byGrid;
    }

    if (expectedGrid) {
        const gs = Number(expectedGrid);
        const anyGrid = list.find((t) => Number(t?.gridSize ?? t?.grid_size) === gs);
        if (anyGrid) return anyGrid;
    }

    return list[0] ?? null;
}

function normalizeScreenshotTemplate(raw) {
    if (!raw || typeof raw !== "object") return null;
    const gridSize = Number(raw.gridSize ?? raw.grid_size);
    const gs = Number.isFinite(gridSize) && gridSize > 0 ? gridSize : 0;
    const startingLayout = parseSlidingStartingLayout(
        raw.startingLayout ?? raw.starting_layout,
        gs,
    );
    return {
        ...raw,
        id: raw.id ?? null,
        gridSize: gs > 0 ? gs : null,
        difficultyLevel: raw.difficultyLevel ?? raw.difficulty_level ?? null,
        startingLayout: startingLayout.length > 0 ? startingLayout : null,
    };
}

/**
 * Load story group + template for a screenshot / animation scene.
 * Same story + board IDs as `/puzzle/play-sliding/:storyId/board/:slidingId`.
 */
export async function loadScreenshotSliding(scene) {
    const { slidingId, storyId, expectedGrid, expectedLevel } = scene;

    if (!storyId) {
        throw new Error("storyId is required for screenshot scenes");
    }

    let group = await SlidingService.getStoryGroup(storyId);
    if (!group?.templates?.length) {
        const admin = await AdminSlidingService.getGroupByStoryId(storyId);
        if (admin?.success && admin.data?.templates?.length) {
            group = admin.data;
        }
    }
    if (!group) {
        throw new Error(`Could not load sliding story ${storyId}`);
    }

    const picked = pickTemplateFromGroup(group?.templates, {
        slidingId,
        expectedGrid,
        expectedLevel,
    });
    if (!picked) {
        throw new Error(
            slidingId
                ? `No sliding board ${slidingId} on story ${storyId}`
                : expectedGrid && expectedLevel
                  ? `No ${expectedGrid}×${expectedGrid} ${expectedLevel} sliding template on story ${storyId}`
                  : `No sliding template on story ${storyId}`,
        );
    }

    const template = normalizeScreenshotTemplate(picked);
    const grid = getTemplateGrid(template);
    if (!template?.startingLayout?.length || grid <= 0) {
        throw new Error(
            slidingId
                ? `Board ${slidingId} is missing grid size or starting layout`
                : "Puzzle starting layout is missing",
        );
    }

    return { group, template, storyId };
}

function groupStoryId(group) {
    return String(group?.storyId ?? group?.story?.id ?? "").trim().toLowerCase();
}

function buildGalleryItem(group, scene, { slidingId = null, storyIdOverride = null } = {}) {
    const sid = groupStoryId(group);
    const sceneStoryId = String(scene.storyId ?? "").trim().toLowerCase();
    const picked = pickTemplateFromGroup(group?.templates, {
        slidingId: slidingId ?? (sid === sceneStoryId ? scene.slidingId : null),
        expectedGrid: scene.expectedGrid ?? 4,
        expectedLevel: scene.expectedLevel ?? "MEDIUM",
    });
    const template = picked ? normalizeScreenshotTemplate(picked) : null;
    const grid = getTemplateGrid(template);
    const hasBoard = Boolean(template?.startingLayout?.length && grid > 0);
    const hasImage = Boolean(getImageUrl(group?.story));
    if (!hasBoard && !hasImage) return null;

    return {
        group,
        template: hasBoard ? template : null,
        storyId: storyIdOverride ?? group.storyId ?? group.story?.id ?? scene.storyId,
    };
}

async function loadPrependGalleryItems(scene) {
    const prepend = Array.isArray(scene.galleryPrepend) ? scene.galleryPrepend : [];
    const items = [];
    for (const entry of prepend) {
        const storyId = entry?.storyId;
        if (!storyId) continue;
        const group = await SlidingService.getStoryGroup(storyId);
        if (!group) continue;
        const item = buildGalleryItem(group, scene, { slidingId: entry.slidingId ?? null });
        if (item) items.push(item);
    }
    return items;
}

/**
 * Load multiple story groups for two-column gallery screenshot scenes.
 * Uses browse-by-tag when `galleryTag` is set; always includes the scene's primary story first.
 */
export async function loadScreenshotGallery(scene) {
    const gridCount = scene.galleryCount ?? 6;
    const tag = (scene.galleryTag ?? scene.label ?? "").toLowerCase().trim();
    const sceneStoryId = String(scene.storyId ?? "").trim().toLowerCase();

    const prependItems = await loadPrependGalleryItems(scene);
    const items = [...prependItems];
    const seen = new Set(prependItems.map((item) => groupStoryId(item.group)));

    let groups = [];
    if (tag) {
        const paged = await SlidingService.listBrowse({
            tags: [tag],
            pageSize: Math.max(gridCount + seen.size, 12),
            page: 0,
            sort: "publishedAt:desc",
        });
        groups = Array.isArray(paged?.content) ? [...paged.content] : [];
    }

    const hasPrimary = groups.some((g) => groupStoryId(g) === sceneStoryId);
    if (!hasPrimary && scene.storyId) {
        const primary = await SlidingService.getStoryGroup(scene.storyId);
        if (primary) groups.unshift(primary);
    } else if (sceneStoryId) {
        const primaryIndex = groups.findIndex((g) => groupStoryId(g) === sceneStoryId);
        if (primaryIndex > 0) {
            const [primary] = groups.splice(primaryIndex, 1);
            groups.unshift(primary);
        }
    }

    for (const group of groups) {
        const sid = groupStoryId(group);
        if (!sid || seen.has(sid)) continue;
        seen.add(sid);

        const item = buildGalleryItem(group, scene);
        if (!item) continue;

        items.push(item);
        if (items.length - prependItems.length >= gridCount) break;
    }

    if (items.length === 0) {
        const single = await loadScreenshotSliding(scene);
        items.push(single);
    }

    return items;
}

export function getStoryTitle(story) {
    return getSlidingStoryTitle(story) || "Sliding puzzle";
}

export function getImageUrl(story) {
    return pickSlidingOriginalImageUrl(story);
}

/**
 * Cover image only — does not require a matching board template.
 * Used by Google Ads showcase mockups (iframes replaced with cover art).
 */
export async function loadScreenshotCoverUrl(scene) {
    const storyId = scene?.storyId;
    if (!storyId) return null;
    try {
        const { group } = await loadScreenshotSliding(scene);
        return getImageUrl(group?.story) || null;
    } catch {
        const group = await SlidingService.getStoryGroup(storyId);
        return getImageUrl(group?.story) || null;
    }
}

export function getTemplateGrid(template) {
    const gs = Number(template?.gridSize ?? template?.grid_size);
    return Number.isFinite(gs) && gs > 0 ? gs : 0;
}

export function getTemplateStartingLayout(template) {
    const grid = getTemplateGrid(template);
    if (grid <= 0) return [];
    return parseSlidingStartingLayout(
        template?.startingLayout ?? template?.starting_layout,
        grid,
    );
}

export function getTemplateDifficultyLevel(template) {
    const level = template?.difficultyLevel ?? template?.difficulty_level;
    return level ? String(level).toUpperCase() : null;
}
